import React, { useEffect, useRef } from "react";
import moment from "moment";
import { getPicklistOptions, isSet } from "../../../../Utils/CommonFunctions";
import { getDateFormat, getUserMoment } from "../../../../Utils/UserFunctions";
import FormInputFields from "../../../../Components/FormInputFields";
import { ErrorBlock } from "../../../../hoc/FormInput";
import {
  extractFieldValue,
  extractOptions,
  getOutputFieldsElement,
  injectVariableValues,
  isValidJson,
  getScmChangeAction,
  getScmOutFocusAction,
  preloadScmChangeAction,
  preloadScmOutFocusAction,
  getScmFieldActionPreloadGeneration,
  triggerScmOnChangeAction,
  triggerScmOutFocusAction,
} from "../../MarkupFunctions";
import SwitchItems from "../../../FormBuilder/Builder/FormInputs/SwitchItems";
import PagesInputFields from "../../../PagesInputFields";
import ScmSubFormItem from "../ScmSubFormItem";
import ResourceInput from "./ResourceInput";
import {
  applyTailgatePresetToServices,
  collectLookupIdentityKeys,
  getDefaultToAddressTailgatePreset,
  isSamePagesLookupIdentity,
  resolvePagesInputSelectedValue,
} from "../../../../Containers/PagesContainer/PagesContainerUtils";
import { parseTailgatePresetFromAttributes } from "../../../../Containers/PagesContainer/pagesTailgatePresetUtils";

// GET FIELDS PROPS ELEMENT
const ScmInputTextField = ({ attributes, elmtParams }) => {
  const { defProps, children } = elmtParams;
  let dynamicVar = {
    ...elmtParams.dynamicVar,
    ...defProps.outputResponse,
  };
  const dataElmt = getOutputFieldsElement(attributes, elmtParams);

  let dataElmtStr;
  if (React.isValidElement(dataElmt)) {
    dataElmtStr = "";
  } else if (isValidJson(dataElmt)) {
    dataElmtStr = JSON.stringify(dataElmt);
  } else if (
    dataElmt === null ||
    dataElmt === undefined ||
    dataElmt === "null" ||
    dataElmt === "undefined"
  ) {
    // Pages empty variables often arrive as the literal string "null".
    dataElmtStr = "";
  } else {
    dataElmtStr = dataElmt;
  }

  const hasLoadedPage = useRef(false);
  const attribsName = attributes.hasOwnProperty("name")
    ? attributes.name
    : "";
  const {
    modType,
    fieldGroups,
    formFields,
    isPreview,
    resourcesList,
    onChangeResourcesFieldHandler,
    fieldFormPayload,
    windowVariableList,
  } = defProps;
  let element = null;
  let updatedAttributes = { ...attributes };
  const rawDefaultValue = isSet(
    attributes?.value || attributes?.defaultvalue,
    ""
  );

  // Apply default value from dynamicVar once (standalone inputs only; form wrapper handles its own rows)
  useEffect(() => {
    if (hasLoadedPage.current) return;
    if (elmtParams.formScope || isPreview === true) return;
    if (!updatedAttributes?.name) return;

    const fieldType = String(attributes?.type || "");
    const isDateField = fieldType === "Date" || fieldType === "DatePick";
    if (isDateField) {
      let userDateFormat = "YYYY-MM-DD";
      try {
        userDateFormat = getDateFormat("Date") || "YYYY-MM-DD";
      } catch (e) {
        userDateFormat = "YYYY-MM-DD";
      }
      const current = fieldFormPayload?.[updatedAttributes.name]?.selected_value;
      const currentText =
        current === null || current === undefined ? "" : String(current);
      const storedDate = moment(
        currentText,
        ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss.S"],
        true
      );
      if (storedDate.isValid()) {
        const dateOnly = storedDate.format(userDateFormat);
        if (dateOnly !== currentText) {
          hasLoadedPage.current = true;
          onChangeResourcesFieldHandler(
            { api_name: updatedAttributes.name },
            dateOnly
          );
          return;
        }
      }
      // Layout date fields use default value "Today" for the user's current date.
      if (String(rawDefaultValue || "").trim().toLowerCase() === "today") {
        const todayMoment = getUserMoment().startOf("day");
        const todayFormatted = todayMoment.format(userDateFormat);
        const hasValue =
          isSet(current, null) !== null && currentText !== "null";
        hasLoadedPage.current = true;
        if (!hasValue) {
          onChangeResourcesFieldHandler(
            { api_name: updatedAttributes.name },
            todayFormatted
          );
          return;
        }
        const parsedCurrent = moment(
          currentText,
          [
            userDateFormat,
            "YYYY-MM-DD",
            "DD-MM-YY",
            "DD-MM-YYYY",
            "YYYY-MM-DDTHH:mm:ss",
          ],
          true
        );
        if (
          parsedCurrent.isValid() &&
          parsedCurrent.startOf("day").isBefore(todayMoment)
        ) {
          onChangeResourcesFieldHandler(
            { api_name: updatedAttributes.name },
            todayFormatted
          );
        }
        return;
      }
    }

    if (typeof rawDefaultValue !== "string" || rawDefaultValue.trim() === "") {
      return;
    }

    const currentVal =
      fieldFormPayload?.[updatedAttributes.name]?.selected_value;
    const isEmpty =
      currentVal === undefined ||
      currentVal === null ||
      currentVal === "" ||
      currentVal === "null";

    // Static default (e.g. defaultvalue="LBS" / "CA") — apply once when empty.
    if (
      rawDefaultValue.indexOf("${") === -1 ||
      rawDefaultValue.indexOf("}") === -1
    ) {
      hasLoadedPage.current = true;
      if (!isEmpty) return;
      let defVal = rawDefaultValue.trim();
      // Resolve country defaults (CA / Canada) to picklist option id.
      const picklistType = String(
        attributes?.picklisttype ||
          attributes?.picklistType ||
          attributes?.picklist_type ||
          ""
      ).toUpperCase();
      if (picklistType === "COUNTRY" || picklistType === "CURRENCY") {
        try {
          const countryOptions = getPicklistOptions({
            picklist_type: picklistType,
          }).map((opt) => ({
            label: opt.value,
            value: opt.id,
          }));
          const lower = defVal.toLowerCase();
          const matched = countryOptions.find(
            (o) =>
              String(o.value || "").toLowerCase() === lower ||
              String(o.label || "").toLowerCase() === lower ||
              ((lower === "ca" || lower === "canada") &&
                String(o.label || "").toLowerCase() === "canada")
          );
          if (matched) {
            defVal = matched.value;
          }
        } catch (e) {
          // keep raw default
        }
      }
      onChangeResourcesFieldHandler(
        { api_name: updatedAttributes.name },
        defVal
      );
      return;
    }

    hasLoadedPage.current = true;

    const cleanedPath = rawDefaultValue
      .trim()
      .replace(/^\$\{\s*/, "")
      .replace(/\}\s*$/, "");

    const updateDefaultValue = extractFieldValue(dynamicVar, cleanedPath);
    onChangeResourcesFieldHandler(
      { api_name: updatedAttributes?.name },
      updateDefaultValue
    );
  }, [rawDefaultValue, dynamicVar, updatedAttributes?.name, onChangeResourcesFieldHandler, elmtParams.formScope, fieldFormPayload, attributes]);

  const scmChangeAction = getScmChangeAction(updatedAttributes);
  const scmOutFocusAction = getScmOutFocusAction(updatedAttributes);
  const applyPagesLookupChange = (value) => {
    const prevSelected =
      fieldFormPayload?.[updatedAttributes?.name]?.selected_value;
    // Same Account re-select: onload may store UUID in value while lookup stores
    // record_id in value + UUID in id — treat any overlapping key as same.
    // Empty prev (onload hydrate / first bind): do NOT clear — otherwise From/To
    // filled by Onload Default Address are wiped before payload catches up.
    const isSameLookupSelection = isSamePagesLookupIdentity(
      prevSelected,
      value
    );
    const hadPrevLookupSelection =
      collectLookupIdentityKeys(prevSelected).size > 0;

    onChangeResourcesFieldHandler(
      { api_name: updatedAttributes?.name },
      value
    );
    const clearFieldsRaw =
      updatedAttributes.clearFields ||
      updatedAttributes.clearfields ||
      updatedAttributes.clear_fields ||
      "";
    if (
      hadPrevLookupSelection &&
      !isSameLookupSelection &&
      clearFieldsRaw &&
      typeof defProps.onClearPagesFieldsHandler === "function"
    ) {
      const names = String(clearFieldsRaw)
        .split(",")
        .map((name) => name.replace(/[${}]/g, "").trim().toLowerCase())
        .filter(Boolean);
      if (names.length) {
        defProps.onClearPagesFieldsHandler(names);
      }
    }
    const tailgatePreset =
      parseTailgatePresetFromAttributes(updatedAttributes) ||
      (String(updatedAttributes?.name || "") === "to_address"
        ? getDefaultToAddressTailgatePreset()
        : null);
    if (
      !elmtParams.formScope &&
      tailgatePreset &&
      value &&
      typeof value === "object" &&
      value.lookupData
    ) {
      const servicesApi =
        tailgatePreset.servicesField || "to_additional_services";
      const nextServices = applyTailgatePresetToServices(
        fieldFormPayload?.[servicesApi]?.selected_value,
        value.lookupData,
        tailgatePreset
      );
      if (nextServices) {
        onChangeResourcesFieldHandler(
          { api_name: servicesApi },
          nextServices
        );
      }
    }
    if (scmChangeAction) {
      triggerScmOnChangeAction(defProps, updatedAttributes, dynamicVar, value);
    }
  };
  // Re-run when left-nav/reload clears the preload Set (generation bumps).
  const preloadGeneration = getScmFieldActionPreloadGeneration();

  useEffect(() => {
    if (isPreview === true || elmtParams.formScope) return;
    if (scmChangeAction) preloadScmChangeAction(defProps, updatedAttributes, dynamicVar);
    if (scmOutFocusAction && scmOutFocusAction !== scmChangeAction) {
      preloadScmOutFocusAction(defProps, updatedAttributes, dynamicVar);
    }
  }, [scmChangeAction, scmOutFocusAction, preloadGeneration]);

  if (isPreview === true) {
    element = (
      <FormInputFields
        {...attributes}
        type="TextInput"
        changeHandler={() => {}}
        value=""
      />
    );
  }

  const obj = extractFieldValue(dynamicVar, attribsName);

  // Check if this is a subform field and route to ScmSubFormItem
  if (isSet(obj, null) !== null && isSet(obj.is_subform_field, false) === true) {
    const subformvar = dynamicVar?.subformvar;

    if (
      subformvar &&
      subformvar.rowData &&
      subformvar.rowIndex !== undefined &&
      subformvar.section
    ) {
      const {
        rowData,
        rowIndex,
        subformIndex,
        section,
        pageType,
        isOpenBox,
        reminderDisplayText,
        onChangeInputAction,
        processFieldData,
      } = subformvar;

      const fieldInSection = obj;
      if (fieldInSection) {
        let processedData;
        if (processFieldData && typeof processFieldData === "function") {
          processedData = processFieldData(fieldInSection);
        } else {
          const columnIndex =
            rowData.subform_column_values?.findIndex(
              (column) => column.table_descriptor_id == fieldInSection.id
            ) ?? -1;

          let columnData = {};
          if (columnIndex !== -1) {
            columnData = rowData.subform_column_values[columnIndex];
          }

          processedData = {
            reminder: isSet(columnData.reminder, ""),
            files: isSet(columnData.files, ""),
            selectedValue: isSet(columnData.selected_value, ""),
            selected_value_text: isSet(columnData.selected_value_text, ""),
            selecteddpnm: isSet(columnData.dpnm, ""),
            overridden: isSet(columnData.overridden, false),
            currency_code: isSet(columnData.currency_code, "CAD"),
            from_currency: isSet(columnData.from_currency, isSet(columnData.currency_code, "CAD")),
            exchange_rate: isSet(columnData.exchange_rate, ""),
            options: isSet(columnData.options, fieldInSection.options),
            columnIndex: columnIndex,
          };
        }

        const subformValidation =
          subformvar.subformValidation ||
          elmtParams.defProps?.validationForms?.[modType] ||
          {};

        element = (
          <ScmSubFormItem
            item={fieldInSection}
            modType={modType}
            keyIndex={fieldInSection.id || attribsName}
            subFormIndex={subformIndex}
            columnIndex={processedData.columnIndex}
            rowIndex={rowIndex}
            pageType={pageType}
            subformValidation={subformValidation}
            reminderDisplayText={reminderDisplayText}
            rowId={rowData.id || ""}
            isOpenBox={isOpenBox}
            section={section}
            reminder={processedData.reminder}
            files={processedData.files}
            selectedValue={processedData.selectedValue}
            selected_value_text={processedData.selected_value_text}
            selecteddpnm={processedData.selecteddpnm}
            overridden={processedData.overridden}
            currency_code={processedData.currency_code}
            from_currency={processedData.from_currency}
            exchange_rate={processedData.exchange_rate}
            options={processedData.options}
            onChangeInputAction={
              fieldInSection?.onChangeInputAction || onChangeInputAction
            }
            elmtParams={elmtParams}
            defProps={defProps}
          />
        );

        return element;
      }
    }
  }

  fieldGroups.forEach((section) => {
    if (section.type !== "subform") {
      section.data.forEach((field, itemIndex) => {
        if (
          field.label?.blocks?.[0]?.text?.toLowerCase() ===
          attribsName.toLowerCase()
        ) {
          const fieldObj = {
            section_index: section.section_index,
            itemIndex: field.itemIndex,
            modType,
            item: {
              ...field,
              modType,
            },
          };

          let appendAttr = {};
          if (isSet(attributes.class, "") !== "") {
            appendAttr.className = isSet(attributes.class, "");
          }

          element = (
            <SwitchItems
              {...defProps}
              key={itemIndex}
              fieldObj={fieldObj}
              item={{
                ...field,
                displayType: isSet(attributes.displaytype, ""),
                customAttr: isSet(attributes, ""),
                modType,
              }}
              inputBlockOnly={true}
              onlyInputComponent={true}
              errorBlock={true}
              isLabel={
                isSet(attributes.withlabel, true) === "false"
                  ? false
                  : Boolean(isSet(attributes.withlabel, true))
              }
              withoutBlock={
                isSet(attributes.inputOnly, true) === "false"
                  ? false
                  : Boolean(isSet(attributes.inputOnly, true))
              }
              section={section}
              {...appendAttr}
            />
          );
        }
      });
    }
  });

  const indexing = formFields.findIndex(
    (i) => attribsName.toLowerCase() === i.label.toLowerCase()
  );
  if (indexing !== -1) {
    if (updatedAttributes.name) {
      delete updatedAttributes.name;
    }
    element = (
      <>
        <FormInputFields {...formFields[indexing]} {...updatedAttributes} />
        <ErrorBlock message={isSet(formFields[indexing].error, "")} />
      </>
    );
  }

  const resourcesIndexing = resourcesList.findIndex(
    (item) =>
      item.variable_name === attributes.name &&
      isSet(item.window_field_id_path, null) !== null
  );

  if (resourcesIndexing !== -1 && element === null) {
    const resource = resourcesList[resourcesIndexing];
    const currentWindowId = resource.window_id;
    const fieldPath = isSet(resource.window_field_id_path, null);
    const windowMeta = isSet(windowVariableList, {})[currentWindowId];

    const resourceElement = (
      <ResourceInput
        attributes={updatedAttributes}
        children={children}
        defProps={defProps}
        resource={resource}
        windowMeta={windowMeta}
        fieldPath={fieldPath}
        fieldFormPayload={fieldFormPayload}
        dataElmtStr={dataElmtStr}
      />
    );

    if (resourceElement) {
      element = resourceElement;
    }
  } else if (element === null && isSet(attributes.type, null) !== null) {
    if (
      attributes.type === "PickList" ||
      attributes.type === "Picklist" ||
      attributes.type === "MultiSelect"
    ) {
      const picklistTypeRaw =
        attributes.picklisttype ||
        attributes.picklistType ||
        attributes.picklist_type;

      if (
        picklistTypeRaw &&
        (attributes.type === "Picklist" || attributes.type === "PickList")
      ) {
        const picklistType = String(picklistTypeRaw).toUpperCase();
        if (picklistType === "COUNTRY" || picklistType === "CURRENCY") {
          const picklistOptions = getPicklistOptions({
            picklist_type: picklistType,
          });
          updatedAttributes = {
            ...updatedAttributes,
            options: picklistOptions.map((opt) => ({
              label: opt.value,
              value: opt.id,
            })),
          };
        }
      } else {
        const options = extractOptions(children, attributes.type);
        if (options.length > 0) {
          updatedAttributes = {
            ...updatedAttributes,
            options: options,
          };
        }
      }
    }

    const payloadSelected =
      fieldFormPayload?.[updatedAttributes.name]?.selected_value;

    const {
      changeAction: _ca,
      changeaction: _ca2,
      clearFields: _cf,
      clearfields: _cf2,
      clear_fields: _cf3,
      outFocus: _of,
      outfocus: _of2,
      picklisttype: _pt,
      picklistType: _pt2,
      picklist_type: _pt3,
      withlabel: _wl,
      withLabel: _wl2,
      with_label: _wl3,
      inputOnly: _io,
      inputonly: _io2,
      input_only: _io3,
      ...inputAttribs
    } = updatedAttributes;

    const withlabelRaw =
      updatedAttributes.withlabel ??
      updatedAttributes.withLabel ??
      updatedAttributes.with_label;
    const inputOnlyRaw =
      updatedAttributes.inputOnly ??
      updatedAttributes.inputonly ??
      updatedAttributes.input_only;
    const showLabel =
      withlabelRaw === true ||
      String(withlabelRaw).toLowerCase() === "true";
    const inputOnly =
      inputOnlyRaw === "false" || inputOnlyRaw === false
        ? false
        : inputOnlyRaw === undefined || inputOnlyRaw === null
          ? true
          : Boolean(inputOnlyRaw);

    // Page HTML required="true" arrives as a string. Layout inputs only
    // add the red bar when required is the boolean true.
    // Conditional required: required="${some_field}" resolves against the
    // live form payload / dynamic vars (e.g. Notify Shipper -> From Email),
    // same ${} resolution already used for defaultvalue above.
    let requiredRaw = inputAttribs.required;
    if (typeof requiredRaw === "string" && requiredRaw.includes("${")) {
      const requiredPath = requiredRaw
        .trim()
        .replace(/^\$\{\s*/, "")
        .replace(/\}\s*$/, "");
      requiredRaw =
        fieldFormPayload?.[requiredPath]?.selected_value ??
        extractFieldValue(dynamicVar, requiredPath);
    }
    const isRequired =
      requiredRaw === true ||
      requiredRaw === "true" ||
      requiredRaw === "required" ||
      requiredRaw === 1 ||
      requiredRaw === "1";

    const resolvedPayload = resolvePagesInputSelectedValue(
      updatedAttributes?.type,
      payloadSelected,
      dataElmtStr
    );

    const pagesField = (
      <PagesInputFields
        {...inputAttribs}
        required={isRequired}
        childrenProps={children}
        fieldFormPayload={fieldFormPayload}
        value={resolvedPayload}
        changeHandler={applyPagesLookupChange}
        blurHandler={
          scmOutFocusAction
            ? (value) => {
                onChangeResourcesFieldHandler(
                  { api_name: updatedAttributes?.name },
                  value
                );
                triggerScmOutFocusAction(
                  defProps,
                  updatedAttributes,
                  dynamicVar,
                  value
                );
              }
            : undefined
        }
      />
    );

    element = inputOnly && !showLabel ? (
      pagesField
    ) : (
      <div
        className={inputOnly ? "scm-pages-input-compact" : "form-group mb-0"}
        style={{ width: "100%" }}
      >
        {showLabel && (
          <label className="mb-1 d-block">
            {updatedAttributes.label ||
              updatedAttributes.title ||
              updatedAttributes.name ||
              attribsName}
          </label>
        )}
        {pagesField}
      </div>
    );
  }

  const lookupEngine = String(
    attributes.lookupengine || attributes.lookupEngine || ""
  ).toLowerCase();
  if (lookupEngine === "layout" && isPreview !== true) {
    const layoutWindow =
      attributes.windowname || attributes.windowName || attributes.window || "Accounts";
    element = (
      <SwitchItems
        {...defProps}
        onChangeInputAction={() => {}}
        onBlurHandlerAction={() => {}}
        failMessage={() => {}}
        syncPagesOnSelect={true}
        onPagesLookupChange={applyPagesLookupChange}
        item={{
          element: "Lookup",
          value: layoutWindow,
          id: updatedAttributes.name || "account",
          modType: "ADDFORM",
          selectedValue: "",
          displayValue: "",
          required: false,
          access_mode: "",
        }}
        fieldObj={{
          section_index: 0,
          itemIndex: 0,
          modType: "ADDFORM",
          item: { element: "Lookup", value: layoutWindow },
        }}
        section={{}}
        validationForms={{ ADDFORM: {} }}
        screen="Pages"
        inputBlockOnly={true}
        onlyInputComponent={true}
        isLabel={false}
        withoutBlock={true}
      />
    );
  }

  return element;
};

ScmInputTextField.defaultProps = {
  attributes: {},
  elmtParams: {},
};

export default ScmInputTextField;
